import * as React from 'react';
import { defaultSmDate } from 'utils/formatter';
import moment from 'moment-timezone';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import {
    AmbientLight,
    COORDINATE_SYSTEM,
    LightingEffect,
    MapView,
    PointLight,
    Position2D,
    RGBAColor,
} from '@deck.gl/core';
import DeckGL from '@deck.gl/react';
import { Auftrag, AuftragFilter, useAuftragList } from 'api/auftrag';
import { ArtikelDetail } from 'api/artikelDetail';
import { PickInfo, ViewStateProps } from '@deck.gl/core/lib/deck';
import { GridCellLayer, TextLayer } from '@deck.gl/layers';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { FullViewIcon } from '@fluentui/react-icons';
import { AuftragPositionFilter, useAuftragPositionList } from 'api/auftragPosition';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector } from 'store/lookups/selector';
import { ChartController } from './ChartController';
import { FilterValues, AxisLabel, Pillar } from './types';
import { ContextMenu } from './ContextMenu';

export const styles = mergeStyleSets({
    container: {
        position: 'relative',
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'hidden',
    },
    resetView: {
        position: 'absolute',
        right: 0,
        top: 0,
        fontSize: 16,
        border: 'none',
        background: '#FFF',
        cursor: 'pointer',
    },
});

const clientColors = [
    [244, 67, 54],
    [63, 81, 181],
    [76, 175, 80],
    [255, 152, 0],
    [233, 30, 99],
    [139, 195, 74],
    [255, 87, 34],
    [3, 169, 244],
    [205, 220, 57],
    [121, 85, 72],
    [156, 39, 176],
    [0, 188, 212],
    [255, 235, 59],
    [103, 58, 183],
    [0, 150, 136],
    [255, 193, 7],
    [96, 125, 139],
];

interface Viewport {
    width: string;
    height: string;
    latitude: number;
    longitude: number;
    zoom: 4;
}

const ambientLight = new AmbientLight({
    color: [255, 255, 255],
    intensity: 2.0,
});

const pointLight1 = new PointLight({
    color: [255, 255, 255],
    intensity: 0.8,
    position: [-10, -10, 800],
});

const pointLight2 = new PointLight({
    color: [255, 255, 255],
    intensity: 0.8,
    position: [10, 10, 800],
});

const lightingEffect = new LightingEffect({ ambientLight, pointLight1, pointLight2 });

interface ChartData {
    data: Array<Pillar>;
    xAxisLabels: Array<AxisLabel>;
    yAxisLabels: Array<AxisLabel>;
}

function getColor(p: Pillar): RGBAColor {
    const clientColor = clientColors[p.x % clientColors.length];

    if (p.value) {
        return [clientColor[0], clientColor[1], clientColor[2], 255];
    }

    if (parseInt(p.month.substring(0, 4), 10) % 2 === 0) {
        return [25, 25, 25, 40];
    }

    return [25, 25, 25, 25];
}

const initialViewport = {
    pitch: 45,
    latitude: 3,
    longitude: 5,
    zoom: 6,
    bearing: 60,
    maxZoom: 25,
    maxPitch: 90,
};

interface ArtikelDetailMonth {
    artikelDetail: ArtikelDetail;
    totalMenge: number;
    auftragList: Auftrag[];
}

export const Artikeluebersicht3d: React.FC<FilterValues> = (filterValues) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);

    const auftragListFilter = React.useMemo<AuftragFilter>(
        () => ({
            datumVon: filterValues.datumVon,
            datumBis: filterValues.datumBis,
            betreuerExact: filterValues.betreuer,
            telBetreuerExact: filterValues.telBetreuer,
            rahmen: false,
            withKunde: true,
        }),
        [filterValues.datumVon, filterValues.datumBis, filterValues.betreuer, filterValues.telBetreuer],
    );

    const auftragList = useAuftragList(auftragListFilter);

    const auftragPositionListFilter = React.useMemo<AuftragPositionFilter>(
        () => ({
            auNrList: (auftragList.data || []).map((auftrag) => auftrag.nr),
            withArtikelDetail: true,
        }),
        [auftragList.data],
    );

    const auftragPositionList = useAuftragPositionList(auftragPositionListFilter);

    const chartContainerRef = React.useRef<HTMLDivElement>(null);

    const [pickInfo, setPickInfo] = React.useState<
        PickInfo<GridCellLayer<Pillar>> | PickInfo<TextLayer<AxisLabel>> | null
    >(null);

    const [viewport, setViewport] = React.useState<ViewStateProps>(initialViewport);

    const getTooltip = <D extends unknown>(
        info: PickInfo<D>,
    ): null | {
        text?: string;
        html?: string;
        className?: string;
        style?: never;
    } => {
        if (!info?.layer) {
            setPickInfo(null);
            return null;
        }

        if (info.layer instanceof GridCellLayer) {
            const entry = (info.object as unknown) as Pillar;

            if (!entry) {
                return null;
            }

            setPickInfo((info as unknown) as PickInfo<GridCellLayer<Pillar>>);
            return null;
        }

        if (info.layer instanceof TextLayer) {
            setPickInfo((info as unknown) as PickInfo<TextLayer<AxisLabel>>);
            return null;
        }

        return null;
    };

    const [layers, setLayers] = React.useState<(GridCellLayer<Pillar> | TextLayer<AxisLabel>)[]>([]);

    const updateViewport = React.useCallback(() => {
        // this needs to be called after updating the layers
        // otherwise DeckGL will occasionally not draw anything
        setTimeout(() => {
            setViewport((v) => ({
                ...v,
            }));
        }, 500);
    }, []);

    const [gridCellLayerData, setGridCellLayerData] = React.useState<ChartData>({
        data: [],
        xAxisLabels: [],
        yAxisLabels: [],
    });

    const startMonth = React.useMemo(() => moment(filterValues.datumVon).startOf('month'), [filterValues.datumVon]);
    const endMonth = React.useMemo(() => moment(filterValues.datumBis).startOf('month'), [filterValues.datumBis]);

    const months = React.useMemo(() => {
        const newMonths: string[] = [];

        const month = startMonth.clone();

        while (month.isSameOrBefore(endMonth)) {
            newMonths.push(month.format('YYYY-MM'));
            month.add({ months: 1 });
        }

        return newMonths.sort((a, b) => b.localeCompare(a));
    }, [startMonth, endMonth]);

    React.useEffect(() => {
        if (!auftragList.data || !auftragPositionList.data) {
            return;
        }

        const xAxisLabels: Array<AxisLabel> = [];
        const yAxisLabels: Array<AxisLabel> = [];
        const data: Array<Pillar> = [];

        let maxTotalMenge = 0;

        const auftragByArtikelDetailMonth: { [key: string]: { [key: string]: ArtikelDetailMonth } } = {};

        const artikelDetailById: { [key: string]: ArtikelDetail } = {};

        auftragList.data.forEach((auftrag) => {
            if (!auftrag.kNr || !auftrag.kunde || !auftrag.datum || auftrag.datum === defaultSmDate) {
                return;
            }

            const month = moment(auftrag.datum).format('YYYY-MM');

            (auftragPositionList.data || []).forEach((auftragPosition) => {
                if (
                    auftragPosition.auNr === auftrag.nr &&
                    auftragPosition.artikelNr &&
                    auftragPosition.artikelGruppeNr &&
                    auftragPosition.artikelDetail &&
                    auftragPosition.artikelGruppeNr !== 97 &&
                    (!filterValues.artikelGruppeNr || filterValues.artikelGruppeNr === auftragPosition.artikelGruppeNr)
                ) {
                    const artikelDetailId = auftragPosition.artikelGruppeNr + '|' + auftragPosition.artikelNr;

                    artikelDetailById[artikelDetailId] = auftragPosition.artikelDetail;

                    if (!auftragByArtikelDetailMonth[artikelDetailId]) {
                        auftragByArtikelDetailMonth[artikelDetailId] = {};
                    }

                    if (!auftragByArtikelDetailMonth[artikelDetailId][month]) {
                        auftragByArtikelDetailMonth[artikelDetailId][month] = {
                            artikelDetail: auftragPosition.artikelDetail,
                            totalMenge: 0,
                            auftragList: [],
                        };
                    }

                    auftragByArtikelDetailMonth[artikelDetailId][month].totalMenge += auftragPosition.posMenge;
                    auftragByArtikelDetailMonth[artikelDetailId][month].auftragList.push(auftrag);

                    if (auftragByArtikelDetailMonth[artikelDetailId][month].totalMenge > maxTotalMenge) {
                        maxTotalMenge = auftragByArtikelDetailMonth[artikelDetailId][month].totalMenge;
                    }
                }
            });
        });

        const sortedArtikelDetailList = Object.values(artikelDetailById).sort((a, b) =>
            ((artikelGruppenByNr[a.artikelGruppeNr]?.text || a.artikelGruppeNr) + '|' + a.artikelNr).localeCompare(
                (artikelGruppenByNr[b.artikelGruppeNr]?.text || b.artikelGruppeNr) + '|' + b.artikelNr,
            ),
        );

        let x = 0;

        const maxHeight = 100;
        const hightScale = 1 / (maxTotalMenge / maxHeight);

        months.forEach((month, index) => {
            xAxisLabels.push({
                type: 'month',
                label: month,
                x: 0,
                y: index,
            });
        });

        sortedArtikelDetailList.forEach((artikelDetail) => {
            const artikelDetailId = artikelDetail.artikelGruppeNr + '|' + artikelDetail.artikelNr;
            let y = 0;

            yAxisLabels.push({
                type: 'artikeldetail',
                label: artikelGruppenByNr[artikelDetail.artikelGruppeNr]?.text + ' - ' + artikelDetail.artikelNr,
                x,
                y: 0,
            });

            months.forEach((month) => {
                const artikelDetailMonth = auftragByArtikelDetailMonth[artikelDetailId]?.[month];
                const value = artikelDetailMonth?.totalMenge || 0;
                data.push({
                    x,
                    y,
                    color: '#1DE69D',
                    artikelGruppeNr: artikelDetail.artikelGruppeNr,
                    artikelNr: artikelDetail.artikelNr,
                    auNrList: (artikelDetailMonth?.auftragList || []).map((a) => a.nr),
                    totalMenge: value,
                    month,
                    value,
                    height: value * hightScale,
                });

                y += 1;
            });

            x += 1;
        });

        setGridCellLayerData({
            data,
            xAxisLabels,
            yAxisLabels,
        });
    }, [artikelGruppenByNr, auftragList.data, auftragPositionList.data, filterValues.artikelGruppeNr, months]);

    React.useEffect(() => {
        const newLayers = [
            new GridCellLayer<Pillar>({
                coordinateSystem: COORDINATE_SYSTEM.METER_OFFSETS,
                id: 'grid-cell-layer',
                data: gridCellLayerData.data,
                extruded: true,
                pickable: true,
                elevationScale: 5000,
                material: true,
                cellSize: 5000,
                getPosition: (d): Position2D => [(d as Pillar).x * 10000, (d as Pillar).y * 6000],
                getColor: (d): RGBAColor => getColor(d as Pillar),
                getElevation: (d): number => (d as Pillar).height,
                highlightColor: [48, 128, 180, 255],
                autoHighlight: true,
            }),

            // months - left
            new TextLayer<AxisLabel>({
                coordinateSystem: COORDINATE_SYSTEM.METER_OFFSETS,
                id: 'text-layer-x-right',
                data: gridCellLayerData.xAxisLabels,
                pickable: true,
                getPosition: (d): Position2D => [((d as AxisLabel).x - 0.2) * 10000, ((d as AxisLabel).y - 0.2) * 6000],
                getText: (d): string => (d as AxisLabel).label,
                getAngle: (): number => 0,
                getSize: 5000,
                sizeUnits: 'meters',
                getColor: [30, 30, 30],
                getTextAnchor: 'end',
                getAlignmentBaseline: 'bottom',
                billboard: false,
            }),

            // kunden - front
            new TextLayer<AxisLabel>({
                coordinateSystem: COORDINATE_SYSTEM.METER_OFFSETS,
                id: 'text-layer-y-front',
                data: gridCellLayerData.yAxisLabels,
                pickable: true,
                getPosition: (d): Position2D => [((d as AxisLabel).x + 0.5) * 10000, ((d as AxisLabel).y - 0.8) * 6000],
                // getText: (d): string => getClientLabel((d as AxisLabel).label),
                getText: (d): string => (d as AxisLabel).label,
                getAngle: (): number => 270,
                getSize: 5000,
                sizeUnits: 'meters',
                getColor: [30, 30, 30],
                getTextAnchor: 'start',
                getAlignmentBaseline: 'top',
                billboard: false,
            }),
        ];
        setLayers(newLayers);

        updateViewport();
    }, [gridCellLayerData, updateViewport]);

    return (
        <div className={styles.container} ref={chartContainerRef}>
            {(auftragList.isLoading || auftragPositionList.isLoading) && <LoadingIndicator />}
            <DeckGL
                viewState={viewport}
                views={[
                    new MapView({
                        controller: ChartController,
                    }),
                ]}
                layers={layers}
                effects={[lightingEffect]}
                onViewStateChange={({ viewState }: { viewState: Viewport }): void => setViewport(viewState)}
                getTooltip={getTooltip}
                style={{ position: 'relative' }}
            />
            <ContextMenu parentRef={chartContainerRef} pickInfo={pickInfo} />
            <button className={styles.resetView} onClick={() => setViewport(initialViewport)}>
                <FullViewIcon />
            </button>
        </div>
    );
};
